import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Plus, IndianRupee, Clock, AlertCircle } from 'lucide-react';

export default function PaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [outstanding, setOutstanding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [form, setForm] = useState({
    invoice_id: '',
    amount_received: '',
    tds_percentage: '0',
    tds_amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_mode: 'bank_transfer',
    reference_number: '',
    remarks: '',
  });
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [paymentsRes, invoicesRes, outstandingRes] = await Promise.all([
        api.get('/finance/payments'),
        api.get('/finance/invoices'),
        api.get('/finance/payments/outstanding'),
      ]);
      setPayments(paymentsRes.data);
      setInvoices(invoicesRes.data.filter(i => !['paid', 'cancelled'].includes(i.status)));
      setOutstanding(outstandingRes.data);
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openRecordPayment = (invoice = null) => {
    setSelectedInvoice(invoice);
    setForm({
      invoice_id: invoice?.id || '',
      amount_received: '',
      tds_percentage: '0',
      tds_amount: '',
      payment_date: new Date().toISOString().split('T')[0],
      payment_mode: 'bank_transfer',
      reference_number: '',
      remarks: '',
    });
    setDialogOpen(true);
  };

  const handleInvoiceChange = (invoiceId) => {
    const invoice = invoices.find(i => i.id === invoiceId);
    setSelectedInvoice(invoice);
    setForm(prev => ({ ...prev, invoice_id: invoiceId }));
  };

  const handleSave = async () => {
    if (!form.invoice_id || !form.amount_received || !form.payment_date) {
      toast.error('Please fill all required fields');
      return;
    }
    setSaving(true);
    try {
      const tdsPct = parseFloat(form.tds_percentage || 0);
      const tdsAmt = parseFloat(form.tds_amount || 0) || 0;
      const res = await api.post('/finance/payments', {
        invoice_id: form.invoice_id,
        amount_received: parseFloat(form.amount_received),
        tds_amount: tdsAmt,
        tds_percentage: tdsPct > 0 ? tdsPct : null,
        payment_date: form.payment_date,
        payment_mode: form.payment_mode,
        reference_number: form.reference_number,
        remarks: form.remarks,
      });
      toast.success(`Payment recorded. Invoice status: ${res.data.invoice_status}`);
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record payment');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (val) => `₹${(val || 0).toLocaleString('en-IN')}`;
  const set = (key, val) => setForm(p => ({ ...p, [key]: val }));

  // When amount_received changes:
  // - If TDS % is "custom" → leave tds_amount alone (user manages it)
  // - Otherwise → TDS auto-derived as (invoice outstanding - amount_received), floor 0
  const handleAmountReceivedChange = (val) => {
    setForm(prev => {
      if (prev.tds_percentage === 'custom') {
        return { ...prev, amount_received: val };
      }
      const outstanding = selectedInvoice?.amount_outstanding || 0;
      const net = parseFloat(val);
      let tdsAmt = '';
      if (outstanding > 0 && !isNaN(net) && net >= 0) {
        const diff = outstanding - net;
        tdsAmt = diff > 0 ? diff.toFixed(2) : '0';
      } else if (val === '' || isNaN(net)) {
        tdsAmt = '';
      }
      return { ...prev, amount_received: val, tds_amount: tdsAmt };
    });
  };

  // When TDS % changes: derive both amount_received and tds_amount from invoice outstanding
  const handleTdsPercentageChange = (pct) => {
    setForm(prev => {
      if (pct === 'custom') {
        return { ...prev, tds_percentage: pct };
      }
      const outstanding = selectedInvoice?.amount_outstanding || 0;
      const pctNum = parseFloat(pct);
      if (outstanding > 0 && pctNum >= 0) {
        const tdsAmt = (outstanding * pctNum / 100).toFixed(2);
        const amountReceived = (outstanding - parseFloat(tdsAmt)).toFixed(2);
        return { ...prev, tds_percentage: pct, tds_amount: tdsAmt, amount_received: amountReceived };
      }
      return { ...prev, tds_percentage: pct };
    });
  };

  const handleTdsAmountChange = (val) => {
    setForm(prev => ({ ...prev, tds_amount: val }));
  };

  const grossApplied = (parseFloat(form.amount_received) || 0) + (parseFloat(form.tds_amount) || 0);

  return (
    <div data-testid="payments-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-2xl font-heading font-bold text-foreground tracking-tight">Payments</h2>
          <p className="text-sm text-muted-foreground font-body mt-1">Track invoice payments and outstanding</p>
        </div>
        <Button onClick={() => openRecordPayment()} className="bg-primary hover:bg-primary/90 rounded-sm" data-testid="record-payment-btn">
          <Plus className="w-4 h-4 mr-2" /> Record Payment
        </Button>
      </div>

      {/* Outstanding Summary */}
      {outstanding && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="p-4 border-border">
            <div className="flex items-center gap-2 mb-1">
              <IndianRupee className="w-4 h-4 text-muted-foreground" />
              <span className="text-xs text-muted-foreground uppercase tracking-widest">Total Outstanding</span>
            </div>
            <p className="text-2xl font-heading font-bold text-foreground">{formatCurrency(outstanding.total_outstanding)}</p>
          </Card>
          <Card className="p-4 border-border border-l-4 border-l-emerald-500">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="w-4 h-4 text-emerald-600" />
              <span className="text-xs text-muted-foreground uppercase tracking-widest">Current</span>
            </div>
            <p className="text-xl font-heading font-bold text-emerald-600">{formatCurrency(outstanding.totals?.current)}</p>
          </Card>
          <Card className="p-4 border-border border-l-4 border-l-amber-500">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="w-4 h-4 text-amber-600" />
              <span className="text-xs text-muted-foreground uppercase tracking-widest">0-30 Days</span>
            </div>
            <p className="text-xl font-heading font-bold text-amber-600">{formatCurrency(outstanding.totals?.['0-30'])}</p>
          </Card>
          <Card className="p-4 border-border border-l-4 border-l-red-500">
            <div className="flex items-center gap-2 mb-1">
              <AlertCircle className="w-4 h-4 text-red-600" />
              <span className="text-xs text-muted-foreground uppercase tracking-widest">60+ Days</span>
            </div>
            <p className="text-xl font-heading font-bold text-red-600">{formatCurrency(outstanding.totals?.['60+'])}</p>
          </Card>
        </div>
      )}

      {/* Outstanding Invoices */}
      {invoices.length > 0 && (
        <div className="mb-6">
          <h3 className="font-heading font-semibold text-foreground mb-3">Outstanding Invoices</h3>
          <div className="border border-border bg-card rounded-sm overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs uppercase tracking-widest">Invoice #</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest">Client</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest">Due Date</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-right">Total</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-right">Outstanding</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest">Status</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map(inv => (
                  <TableRow key={inv.id}>
                    <TableCell className="font-mono text-sm">{inv.invoice_number}</TableCell>
                    <TableCell>{inv.client_name}</TableCell>
                    <TableCell className={new Date(inv.due_date) < new Date() ? 'text-red-600' : ''}>{inv.due_date}</TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(inv.total_amount)}</TableCell>
                    <TableCell className="text-right font-mono text-amber-600">{formatCurrency(inv.amount_outstanding)}</TableCell>
                    <TableCell>
                      <Badge className={inv.status === 'overdue' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>
                        {inv.status.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="outline" size="sm" onClick={() => openRecordPayment(inv)} className="rounded-sm">
                        Record Payment
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Recent Payments */}
      <h3 className="font-heading font-semibold text-foreground mb-3">Recent Payments</h3>
      <div className="border border-border bg-card rounded-sm overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs uppercase tracking-widest">Date</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Invoice #</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Client</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-right">Net Received</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-right">TDS</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Mode</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Reference</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
            ) : payments.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No payments recorded yet</TableCell></TableRow>
            ) : payments.map(p => (
              <TableRow key={p.id}>
                <TableCell>{p.payment_date}</TableCell>
                <TableCell className="font-mono text-sm">{p.invoice_number}</TableCell>
                <TableCell>{p.client_name}</TableCell>
                <TableCell className="text-right font-mono text-emerald-600 font-medium">{formatCurrency(p.amount_received)}</TableCell>
                <TableCell className="text-right font-mono text-amber-600">
                  {p.tds_amount > 0 ? (
                    <span title={p.tds_percentage ? `${p.tds_percentage}%` : ''}>
                      {formatCurrency(p.tds_amount)}
                      {p.tds_percentage ? <span className="text-xs text-muted-foreground ml-1">({p.tds_percentage}%)</span> : null}
                    </span>
                  ) : '-'}
                </TableCell>
                <TableCell className="capitalize">{p.payment_mode?.replace('_', ' ')}</TableCell>
                <TableCell className="font-mono text-xs">{p.reference_number || '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Record Payment Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Record Payment</DialogTitle>
            <DialogDescription>Enter payment details received from client.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Invoice *</Label>
              <Select value={form.invoice_id} onValueChange={handleInvoiceChange}>
                <SelectTrigger className="rounded-sm">
                  <SelectValue placeholder="Select invoice" />
                </SelectTrigger>
                <SelectContent>
                  {invoices.map(i => (
                    <SelectItem key={i.id} value={i.id}>
                      {i.invoice_number} - {i.client_name} ({formatCurrency(i.amount_outstanding)} due)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedInvoice && (
              <div className="bg-muted/50 p-3 rounded-sm text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Total Amount:</span><span className="font-mono">{formatCurrency(selectedInvoice.total_amount)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Amount Paid:</span><span className="font-mono text-emerald-600">{formatCurrency(selectedInvoice.amount_paid)}</span></div>
                <div className="flex justify-between font-medium border-t border-border mt-2 pt-2">
                  <span>Outstanding:</span>
                  <span className="font-mono text-amber-600">{formatCurrency(selectedInvoice.amount_outstanding)}</span>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">TDS %</Label>
                <Select value={form.tds_percentage} onValueChange={handleTdsPercentageChange}>
                  <SelectTrigger className="rounded-sm" data-testid="tds-percentage-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Auto (from Net)</SelectItem>
                    <SelectItem value="1">1%</SelectItem>
                    <SelectItem value="2">2%</SelectItem>
                    <SelectItem value="10">10%</SelectItem>
                    <SelectItem value="custom">Custom Amount</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">TDS Amount</Label>
                <div className="relative">
                  <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="number"
                    value={form.tds_amount}
                    onChange={e => handleTdsAmountChange(e.target.value)}
                    className="pl-10 rounded-sm"
                    placeholder="0"
                    disabled={form.tds_percentage !== 'custom'}
                    data-testid="tds-amount-input"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Amount Received (Net) *</Label>
                <div className="relative">
                  <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input 
                    type="number" 
                    value={form.amount_received} 
                    onChange={e => handleAmountReceivedChange(e.target.value)} 
                    className="pl-10 rounded-sm" 
                    placeholder={selectedInvoice?.amount_outstanding?.toString() || '0'}
                    data-testid="amount-received-input"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Payment Date *</Label>
                <Input type="date" value={form.payment_date} onChange={e => set('payment_date', e.target.value)} className="rounded-sm" />
              </div>
            </div>

            {grossApplied > 0 && (
              <div className="bg-primary/5 border border-primary/20 p-3 rounded-sm text-sm" data-testid="gross-applied-summary">
                <div className="flex justify-between text-muted-foreground">
                  <span>Net Received:</span>
                  <span className="font-mono">{formatCurrency(parseFloat(form.amount_received) || 0)}</span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>+ TDS Deducted:</span>
                  <span className="font-mono">{formatCurrency(parseFloat(form.tds_amount) || 0)}</span>
                </div>
                <div className="flex justify-between font-medium border-t border-border mt-2 pt-2">
                  <span>Total Applied to Invoice:</span>
                  <span className="font-mono text-primary">{formatCurrency(grossApplied)}</span>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Payment Mode</Label>
                <Select value={form.payment_mode} onValueChange={v => set('payment_mode', v)}>
                  <SelectTrigger className="rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Reference #</Label>
                <Input value={form.reference_number} onChange={e => set('reference_number', e.target.value)} className="rounded-sm" placeholder="Transaction ID" />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Remarks</Label>
              <Textarea value={form.remarks} onChange={e => set('remarks', e.target.value)} className="rounded-sm" rows={2} placeholder="Optional notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 rounded-sm">
              {saving ? 'Recording...' : 'Record Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
